import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import PopUpBase from '@components/PopUpBase';
import { pushError, pushSuccess } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import { taskHttpController, taskTypeHttpController } from '@controllers/index';
import { defaultPagination, Paging } from '@Core';
import { ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import { CameraTaskType } from '@EcamModel/model/CameraTaskType';
import { IPopUp } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, Stack, TextField, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerValue } from '@pages/cameras/add-new';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { autocompleteStyles } from '@pages/overview/smartFilterHighLight/components/BaseAutoCompleteForTypeMapping';
import { LoadingProps } from '@pages/task-management';
import {
    cameraPriorityTask,
    cameraStatusTask,
    cameraSubTask,
    CameraSubTasks,
    mappingStatusTask,
} from '@pages/task-management/config';
import color from '@theme/Colors';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

type Props = IPopUp & {
    isShowTaskManagement?: boolean;
    cameraId: number;
    cameraName: string;
    locationName: string;
    setCameraConfigMonitoring?: Dispatch<SetStateAction<ConsolidateCameraMonitoring[]>>;
};

type FormValues = {
    location?: any;
    cameraName?: string;
    priority?: TaskPriority;
    status: TaskStatus;
    subTask?: CameraSubTasks[];

    taskName: string;
    problem?: string;
    solution?: string;
    assign?: string;

    createDate?: Moment;
    endDate: Moment;
    faultStartDate?: Moment;
};

export default function PopupCreateTask(props: Props) {
    const { open, onClose, cameraId, cameraName, locationName, setCameraConfigMonitoring } = props;
    const queryClient = useQueryClient();
    const geoZonesContext = useCameraDetailContext();
    const { refetchTrailAuditEvent } = geoZonesContext;

    const [loadingState, setLoadingState] = useState<LoadingProps>({
        getData: true,
        popupConfirm: false,
    });

    const [page, setPage] = useState<number>(1);
    const maxTotalPagesRef = useRef<number>(page);

    const [listTaskType, setListTaskType] = useState<Paging<CameraTaskType>>(defaultPagination());
    const debounceRef = useRef<_.DebouncedFunc<(value: string) => void>>();

    const valuesDefault: FormValues = {
        location: locationName,
        cameraName: cameraName,
        priority: cameraPriorityTask[1].Value,
        status: cameraStatusTask[0].Value,
        taskName: '',
        assign: undefined,
        problem: undefined,
        solution: undefined,
        createDate: moment(),
        endDate: moment(),
        faultStartDate: moment(),
        subTask: [],
    };

    const {
        handleSubmit,
        watch,
        reset,
        control,
        setValue,
        setError,
        clearErrors,
        getValues,
        formState: { isDirty, isValid, errors, dirtyFields, isSubmitting },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: valuesDefault,
    });

    const disabled = !(isValid && isDirty);

    const setPartialLoading = (loadings: Partial<LoadingProps>) => {
        setLoadingState((prev) => ({ ...prev, ...loadings }));
    };

    const handleCancel = () => {
        onClose?.();
        setPage(1);
        setListTaskType(defaultPagination());
    };

    const upsertCameraTaskAPI = async (_task: CameraTask) => {
        const response = await taskHttpController.upsert(_task);
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraTaskAPI, {
        onSuccess: async (res) => {
            pushSuccess('Task created successfully');
            queryClient.invalidateQueries(['listTask']);
            if (props.isShowTaskManagement) {
                await refetchTrailAuditEvent();
            }

            setCameraConfigMonitoring?.((prev) =>
                prev?.map((item) =>
                    item.CameraId === cameraId
                        ? {
                              ...item,
                              CameraTaskCount: item.CameraTaskCount + 1,
                              CameraTaskOverDueCount: moment(res.EndDate).isSame(moment(), 'day')
                                  ? item.CameraTaskOverDueCount + 1
                                  : item.CameraTaskOverDueCount,
                          }
                        : item
                )
            );
        },
        onError: (err: AxiosError) => pushError(err.message),
        onSettled: () => {
            setPartialLoading({ popupConfirm: false });
        },
    });

    const handleConfirmUpsertTask = async (data: FormValues) => {
        setPartialLoading({ popupConfirm: true });
        const _task: CameraTask = {
            CameraId: cameraId!,
            Assignee: data?.assign,
            TaskName: data?.taskName,
            TaskPriority: data?.priority!,
            Problem: data?.problem,
            Solution: data?.solution,
            TaskStatus: data.status,
            EndDate: moment(data?.endDate).toDate(),
            Created: moment(data?.createDate).toDate(),
            FaultStartDate: moment(data?.faultStartDate)?.toDate(),
            SubTask: data?.subTask?.length ? JSON.stringify(data?.subTask) : undefined,
        };

        await mutationUpsertCameraNote.mutateAsync(_task).finally(() => handleCancel());
    };

    const handleGetOptionTaskTypes = (valueSearch?: string) => {
        setPartialLoading({ getData: true });

        taskTypeHttpController
            .list({
                pageSize: 12,
                page: page || 1,
                search: {
                    fields: ['Name'],
                    content: valueSearch || '',
                },
            })
            .then((res) => {
                if (res) {
                    maxTotalPagesRef.current = Math.max(maxTotalPagesRef.current, res.totalPages);

                    setListTaskType((prevList) => {
                        const newRows = res.rows.filter(
                            (newItem) => !prevList.rows.some((existingItem) => existingItem.Id === newItem.Id)
                        );
                        return {
                            ...res,
                            rows: [...prevList.rows, ...newRows],
                        };
                    });
                }
            })
            .finally(() => {
                setPartialLoading({ getData: false });
            });
    };

    const handleAddTaskType = (value: string) => {
        taskTypeHttpController
            .upsert({
                Name: value,
            })
            .then((res) => {
                setListTaskType((prev) => ({
                    ...prev,
                    rows: prev.rows.some((task) => task.Id === res.Id)
                        ? prev.rows.map((task) => (task.Id === res.Id ? res : task))
                        : [...prev.rows, res],
                }));
            })
            .catch((err) => {})
            .finally(() => {});
    };

    const handleInputChange = useCallback((event: React.SyntheticEvent, value: string) => {
        debounceRef.current?.(value);
    }, []);

    // useEffect(() => {
    //     debounceRef.current = _.debounce((inputValue: string) => {
    //         const hasDuplicate = listTaskType.rows?.some(
    //             (item) => item.Name.toLowerCase() === inputValue.toLowerCase()
    //         );
    //         if (!hasDuplicate && page < maxTotalPagesRef.current) {
    //             handleGetOptionTaskTypes(inputValue);
    //         }
    //     }, 1000);
    // }, [page]);

    // useEffect(() => {
    //     if (!open) return;
    //     handleGetOptionTaskTypes();
    // }, [open, page]);

    useEffect(() => {
        if (!open) return;
        reset(valuesDefault);
    }, [open]);

    return (
        <PopUpBase
            title={<Typography variant="h4">Create task for camera: {cameraName}</Typography>}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: 750,
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            open={open}
            onClose={onClose}
            hideConfirm
            minWidthButton={130}
            desc={
                <Stack direction={'column'} spacing={2} bgcolor={color.grey100} borderRadius={'4px'} p={2}>
                    {/* location - camera */}
                    <Stack direction={'row'} spacing={2} width={'100%'}>
                        <Stack width={'50%'}>
                            <Controller
                                name="location"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                Location<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack width={'50%'}>
                            <Controller
                                name="cameraName"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                Camera<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>

                    {/* task name */}
                    <Stack width={'100%'}>
                        <Controller
                            name="taskName"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please enter a task.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    required
                                    autoFocus
                                    placeholder={'Task'}
                                    label="Task"
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    value={field.value}
                                />
                            )}
                        />
                    </Stack>

                    {/* subTask */}
                    <Stack width={'100%'}>
                        <Controller
                            name="subTask"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <IMultiSelectWithCheckBox
                                    {...field}
                                    limitTags={3}
                                    label={'Sub task'}
                                    keyId={'Name'}
                                    keyLabel={'Name'}
                                    placeHolder="Select sub task"
                                    value={field.value}
                                    listData={cameraSubTask}
                                    setFilter={field.onChange}
                                    chipSize={250}
                                />
                            )}
                        />
                    </Stack>

                    {/* problem */}
                    {/* <Stack width={'100%'}>
                        <Controller
                            name="problem"
                            control={control}
                            rules={{
                                maxLength: {
                                    value: 1000,
                                    message: 'Problem should not exceed 1000 characters.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Problem"
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    multiline
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            borderRadius: 0,
                                        },
                                    }}
                                    rows={6}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Enter problem"
                                />
                            )}
                        />
                        <Typography
                            fontSize={12}
                            color={watch().problem?.length > 1000 ? color.danger : color.textPrimary}
                            mt={'4px'}
                            textAlign={'end'}
                        >
                            {watch().problem?.length} / 1000
                        </Typography>
                    </Stack> */}

                    {/* solution */}
                    {/* <Stack width={'100%'}>
                        <Controller
                            name="solution"
                            control={control}
                            rules={{
                                maxLength: {
                                    value: 1000,
                                    message: 'Solution should not exceed 1000 characters.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Solution"
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    multiline
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            borderRadius: 0,
                                        },
                                    }}
                                    rows={6}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Enter solution"
                                />
                            )}
                        />
                        <Typography
                            fontSize={12}
                            color={watch().solution?.length > 1000 ? color.danger : color.textPrimary}
                            mt={'4px'}
                            textAlign={'end'}
                        >
                            {watch().solution?.length} / 1000
                        </Typography>
                    </Stack> */}

                    {/* assign */}
                    <Stack width={'100%'} direction={'column'} spacing={1}>
                        <Controller
                            name="assign"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    placeholder={'Assignee'}
                                    label="Assignee"
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    value={field.value}
                                />
                            )}
                        />
                    </Stack>

                    {/* createDate - endDate  */}
                    {/* <Stack direction={'row'} spacing={2} width={'100%'}> 
                        <Stack width={'50%'}>
                            <Controller
                                name="createDate"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={
                                            <span>
                                                Create date<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        readOnly
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack> 
                    </Stack>  */}

                    {/* faultStartDate - taskType */}
                    {/* <Stack direction={'row'} spacing={2} width={'100%'}>
                        <Stack width={'50%'}>
                            <Controller
                                name="faultStartDate"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={<span>Fault start date</span>}
                                        value={field.value}
                                        format="DD/MM/YYYY"
                                        shouldDisableDate={(date) => disableFutureDates(moment(date).toDate())}
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack width={'50%'}>
                            <Controller
                                name="subTask"
                                control={control}
                                render={({ field, fieldState: { error } }) => {
                                    return (
                                        <>
                                            <ICustomFreeSoloMultiSelectWithCheckBox
                                                pageNumber={page}
                                                totalPages={listTaskType.totalPages}
                                                options={listTaskType.rows}
                                                loading={loadingState.getData}
                                                placeholder="Select sub tasks"
                                                label="Sub task"
                                                value={field.value || []}
                                                onChangeValue={field.onChange}
                                                getOptionLabel={(option) => option.Name || ''}
                                                onSetPage={setPage}
                                                onInputChange={handleInputChange}
                                                handleAddItem={handleAddTaskType}
                                            />
                                        </>
                                    );
                                }}
                            />
                        </Stack>
                    </Stack> */}

                    {/* dueDate - priority */}
                    <Stack direction={'row'} spacing={2} width={'100%'}>
                        <Stack width={'50%'}>
                            <Controller
                                name="endDate"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: 'Please choose a date.',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <DesktopDatePicker
                                        {...field}
                                        label={
                                            <span>
                                                Due date<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        value={field.value}
                                        format="DD/MM/YYYY"
                                        shouldDisableDate={(date) =>
                                            validateDatePickerValue(moment(date).toDate()) !== null
                                        }
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack width={'50%'}>
                            <Controller
                                name="status"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        {...field}
                                        options={cameraStatusTask
                                            .filter(
                                                (task) =>
                                                    task.Value !== TaskStatus.Completed &&
                                                    task.Value !== TaskStatus.Cancelled
                                            )
                                            .map((option) => option.Value)}
                                        value={field.value}
                                        getOptionLabel={(option) => mappingStatusTask[option]}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputLabelProps={{ shrink: true }}
                                                label={
                                                    <span>
                                                        Status<span style={{ color: 'red' }}>{`*`}</span>
                                                    </span>
                                                }
                                                placeholder="Select status"
                                            />
                                        )}
                                        clearIcon={<></>}
                                        sx={{
                                            ...autocompleteStyles,
                                            '& .MuiAutocomplete-endAdornment': {
                                                top: '50%',
                                            },
                                        }}
                                        onChange={(_, val) => {
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loadingState.popupConfirm}
                        loadingPosition="center"
                        variant="contained"
                        disabled={disabled || loadingState.popupConfirm}
                        onClick={handleSubmit((data) => handleConfirmUpsertTask(data))}
                    >
                        Create
                    </LoadingButton>
                </Stack>
            }
        />
    );
}
